/**
 * Chapter editor (admin). Route: /admin/courses/:track/:themeSlug/:chapterSlug
 *
 * Loads the chapter, mounts the rich editor, debounces autosave (1s) of
 * `content`, and exposes Draft <-> Published toggle. Save state appears in
 * the header with subtle text ("Saving…", "Saved at HH:MM").
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle, CircleDashed, FloppyDisk } from "@phosphor-icons/react";
import { coursesApi, coursesAdminApi } from "../../api/courses";
import { getTrackLabel } from "../../lib/tracks";
import ChapterRichEditor from "../../components/courses/ChapterRichEditor";

const AUTOSAVE_MS = 1200;

export default function AdminChapterEditor() {
  const { t } = useTranslation();
  const { track, themeSlug, chapterSlug } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [content, setContent] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await coursesApi.chapter(track, themeSlug, chapterSlug);
      setBundle(res);
      setContent(res.chapter.content || { type: "doc", content: [] });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [track, themeSlug, chapterSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const persist = useCallback(
    async (nextContent) => {
      if (!bundle) return;
      setSaveStatus("saving");
      try {
        await coursesAdminApi.updateChapter(bundle.chapter.id, {
          content: nextContent,
        });
        setSaveStatus("saved");
        setSavedAt(new Date());
      } catch {
        setSaveStatus("error");
      }
    },
    [bundle],
  );

  const onContentChange = useCallback(
    (next) => {
      setContent(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => persist(next), AUTOSAVE_MS);
    },
    [persist],
  );

  const togglePublish = useCallback(async () => {
    if (!bundle) return;
    const next = bundle.chapter.status === "published" ? "draft" : "published";
    const updated = await coursesAdminApi.updateChapter(bundle.chapter.id, {
      status: next,
    });
    setBundle((b) => ({ ...b, chapter: { ...b.chapter, status: updated.status } }));
  }, [bundle]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !bundle) {
    return <p className="text-sm text-red-600">{t("admin.courses.chapter_not_found")}</p>;
  }

  const { theme, chapter } = bundle;
  const isPublished = chapter.status === "published";

  return (
    <div data-testid="admin-chapter-editor-page" className="space-y-6">
      <Link
        to={`/admin/courses/${track}/${themeSlug}`}
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
        data-testid="admin-chapter-back"
      >
        <ArrowLeft size={14} weight="bold" />
        {getTrackLabel(track, t)} · {theme.title}
      </Link>

      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("admin.courses.chapter_kicker")}
          </p>
          <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            {chapter.title}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <span
            data-testid="admin-chapter-save-status"
            className="text-xs text-slate-500 inline-flex items-center gap-1.5"
          >
            {saveStatus === "saving" && (
              <>
                <FloppyDisk size={14} className="animate-pulse" />
                {t("admin.courses.saving")}
              </>
            )}
            {saveStatus === "saved" && savedAt && (
              <>
                <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                {t("admin.courses.saved_at", {
                  time: savedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              </>
            )}
            {saveStatus === "error" && (
              <span className="text-red-600">{t("admin.courses.save_error")}</span>
            )}
          </span>

          <button
            type="button"
            onClick={togglePublish}
            data-testid="admin-chapter-toggle-publish"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all ${
              isPublished
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
          >
            {isPublished ? (
              <>
                <CheckCircle size={14} weight="fill" />
                {t("admin.courses.status_published")}
              </>
            ) : (
              <>
                <CircleDashed size={14} weight="bold" />
                {t("admin.courses.status_draft")}
              </>
            )}
          </button>
        </div>
      </header>

      <ChapterRichEditor
        value={content}
        onChange={onContentChange}
        placeholder={t("admin.courses.editor_placeholder")}
      />
    </div>
  );
}
